import { create } from 'zustand';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

import { api } from '@/lib/api';
import { formatIsoWeek } from '@/lib/week';

dayjs.extend(isoWeek);

/**
 * Generate a list of the last N ISO weeks descending — fallback when API is unreachable.
 *
 * PR 9.2.1 B2: 输出走 ``formatIsoWeek`` (2-digit padded), 不再手写
 * ``${year}-W${week}`` 模板. cursor 链 ``dayjs().subtract(1, 'week')`` 不变.
 */
function fallbackWeeks(n = 26): string[] {
  const out: string[] = [];
  // PR 9.2g(Minor 1): 从最近 completed 周起,兜底也不含未结束周。
  let cursor = dayjs().subtract(1, 'week');
  if (dayjs().isoWeekday() === 1 && dayjs().hour() < 8) {
    cursor = cursor.subtract(1, 'week');
  }
  for (let i = 0; i < n; i += 1) {
    out.push(formatIsoWeek(cursor.isoWeekYear(), cursor.isoWeek()));
    cursor = cursor.subtract(1, 'week');
  }
  return out;
}

/**
 * PR 9.2g(Codex Minor 1): 最近 **completed** ISO 周(下周一 08:00 后才算完成)。
 *
 * 当前进行中的周永不 completed → 默认/兜底从上一周起,避免 Brands/Models 列表
 * (其 backend list 接口无 week-gate)在 load 前 / API 失败时落到未结束周。
 * 注:前端用本地时区近似 08:00 边界;真值由 backend ``/api/v2/weeks`` completed-only
 * 决定(load 后 ``week=weeks[0]`` 覆盖此默认)。
 */
function latestCompletedIsoWeek(): string {
  const now = dayjs();
  let candidate = now.subtract(1, 'week'); // 当前周永未完成 → 从上一周起
  if (now.isoWeekday() === 1 && now.hour() < 8) {
    candidate = candidate.subtract(1, 'week'); // 周一 08:00 前,上一周也未开放
  }
  return formatIsoWeek(candidate.isoWeekYear(), candidate.isoWeek());
}

/** PR 9.2g /api/v2/weeks/history?limit= 单项。 */
export interface WeekHistoryItem {
  week: string;
  completed: boolean;
  has_metrics: boolean;
  status: 'ready' | 'data_not_ready';
}

interface WeekState {
  /** Currently selected report week, e.g. "2026-W20". */
  week: string;
  /** ISO weeks that actually have data in DB (descending). Empty until loaded. */
  availableWeeks: string[];
  /** PR 9.2g: /api/v2/weeks/history payload(8 周 + has_metrics + status)。 */
  history: WeekHistoryItem[];
  /** True once loadAvailableWeeks has resolved (success or fallback). */
  isLoaded: boolean;
  /** Setter for the selected week. */
  setWeek: (w: string) => void;
  /**
   * Fetch DB-driven week list from /api/v2/weeks and default `week` to the
   * most recent one present. On failure (404/network) falls back to
   * calendar-based **completed** weeks so the UI still functions.
   */
  loadAvailableWeeks: () => Promise<void>;
  /** PR 9.2g: 拉 /api/v2/weeks/history(缺周 placeholder 用,非关键,silent fail)。 */
  fetchHistory: (limit?: number) => Promise<void>;
  /** Recent weeks for the selector dropdown — DB-driven if loaded, else fallback. */
  recentWeeks: (n?: number) => string[];
}

export const useWeekStore = create<WeekState>((set, get) => ({
  week: latestCompletedIsoWeek(),
  availableWeeks: [],
  history: [],
  isLoaded: false,
  setWeek: (w) => set({ week: w }),
  loadAvailableWeeks: async () => {
    try {
      const res = await api.get<string[]>('/weeks');
      const weeks = res.data;
      if (Array.isArray(weeks) && weeks.length > 0) {
        set({ availableWeeks: weeks, week: weeks[0], isLoaded: true });
        return;
      }
    } catch {
      /* fall through to fallback */
    }
    set({ availableWeeks: fallbackWeeks(), isLoaded: true });
  },
  fetchHistory: async (limit = 8) => {
    try {
      const res = await api.get<WeekHistoryItem[]>('/weeks/history', { params: { limit } });
      if (Array.isArray(res.data)) {
        set({ history: res.data });
      }
    } catch {
      /* history not critical — silent fail */
    }
  },
  recentWeeks: (n = 26) => {
    const s = get();
    if (s.availableWeeks.length > 0) {
      return s.availableWeeks.slice(0, n);
    }
    return fallbackWeeks(n);
  },
}));
